import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

// Import the pwa-install element
import "@khmyznikov/pwa-install";

// Define the props for the component
interface PwaInstallProps {
  manualApple?: boolean;
  manualChrome?: boolean;
  disableChrome?: boolean;
  disableClose?: boolean;
  useLocalStorage?: boolean;
  installDescription?: string;
  disableInstallDescription?: boolean;
  disableScreenshots?: boolean;
  disableScreenshotsApple?: boolean;
  disableScreenshotsChrome?: boolean;
  disableAndroidFallback?: boolean;
  manifestUrl?: string;
  name?: string;
  description?: string;
  icon?: string;
  // Event callbacks expect CustomEvent to access `detail`
  onInstallSuccess?: (event: CustomEvent) => void;
  onInstallFail?: (event: CustomEvent) => void;
  onInstallAvailable?: (event: CustomEvent) => void;
  onUserChoiceResult?: (event: CustomEvent) => void;
  onInstallHowTo?: (event: CustomEvent) => void;
  onInstallGallery?: (event: CustomEvent) => void;
}

// Define the methods that can be called on the component
export interface PwaInstallHandle {
  install: () => void;
  showDialog: (forced?: boolean) => void;
  hideDialog: () => void;
  getInstalledRelatedApps: () => Promise<any>;
  userChoiceResult: string;
  isDialogHidden: boolean;
  isInstallAvailable: boolean;
  isAppleMobilePlatform: boolean;
  isAppleDesktopPlatform: boolean;
  isUnderStandaloneMode: boolean;
  isRelatedAppsInstalled: boolean;
}

const PwaInstall = forwardRef<PwaInstallHandle, PwaInstallProps>(
  (props, ref) => {
    const pwaInstallRef = useRef<HTMLElement & PwaInstallHandle>(null);

    // Expose the component's methods via the ref
    useImperativeHandle(ref, () => ({
      install: () => {
        pwaInstallRef.current?.install();
      },
      showDialog: (forced?: boolean) => {
        pwaInstallRef.current?.showDialog(forced);
      },
      hideDialog: () => {
        pwaInstallRef.current?.hideDialog();
      },
      getInstalledRelatedApps: async () => {
        return await pwaInstallRef.current?.getInstalledRelatedApps();
      },
      userChoiceResult: pwaInstallRef.current?.userChoiceResult || "",
      isDialogHidden: pwaInstallRef.current?.isDialogHidden || true,
      isInstallAvailable: pwaInstallRef.current?.isInstallAvailable || false,
      isAppleMobilePlatform:
        pwaInstallRef.current?.isAppleMobilePlatform || false,
      isAppleDesktopPlatform:
        pwaInstallRef.current?.isAppleDesktopPlatform || false,
      isUnderStandaloneMode:
        pwaInstallRef.current?.isUnderStandaloneMode || false,
      isRelatedAppsInstalled:
        pwaInstallRef.current?.isRelatedAppsInstalled || false,
    }));

    // Effect for adding and removing event listeners
    useEffect(() => {
      const pwaInstallElement = pwaInstallRef.current;
      if (!pwaInstallElement) return;

      const {
        onInstallSuccess,
        onInstallFail,
        onInstallAvailable,
        onUserChoiceResult,
        onInstallHowTo,
        onInstallGallery,
      } = props;

      // A map of the event names to their corresponding callback props
      const eventCallbackMap = {
        "pwa-install-success-event": onInstallSuccess,
        "pwa-install-fail-event": onInstallFail,
        "pwa-install-available-event": onInstallAvailable,
        "pwa-user-choice-result-event": onUserChoiceResult,
        "pwa-install-how-to-event": onInstallHowTo,
        "pwa-install-gallery-event": onInstallGallery,
      };

      // Store the listener functions to ensure the same reference is used for removal
      const listeners: { [key: string]: (event: Event) => void } = {};

      // Add event listeners
      Object.entries(eventCallbackMap).forEach(([eventName, callback]) => {
        if (callback) {
          // Create a listener that casts the event to CustomEvent
          const listener = (event: Event) => {
            callback(event as CustomEvent);
          };
          listeners[eventName] = listener;
          pwaInstallElement.addEventListener(eventName, listener);
        }
      });

      // Cleanup: remove event listeners
      return () => {
        Object.entries(listeners).forEach(([eventName, listener]) => {
          pwaInstallElement.removeEventListener(eventName, listener);
        });
      };
    }, [props]); // Rerun effect if any prop changes

    // Convert camelCase props to kebab-case attributes for the web component
    const attributes = {
      "manual-apple": props.manualApple,
      "manual-chrome": props.manualChrome,
      "disable-chrome": props.disableChrome,
      "disable-close": props.disableClose,
      "use-local-storage": props.useLocalStorage,
      "install-description": props.installDescription,
      "disable-install-description": props.disableInstallDescription,
      "disable-screenshots": props.disableScreenshots,
      "disable-screenshots-apple": props.disableScreenshotsApple,
      "disable-screenshots-chrome": props.disableScreenshotsChrome,
      "disable-android-fallback": props.disableAndroidFallback,
      "manifest-url": props.manifestUrl,
      name: props.name,
      description: props.description,
      icon: props.icon,
    };

    /* @ts-ignore: suppress TS check */
    return <pwa-install ref={pwaInstallRef} {...attributes}></pwa-install>;
  }
);

export default PwaInstall;
